import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Sparkles, Send, X, Loader2, MessageSquare, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../lib/api';

const AISearchAssistant = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestedListings, setSuggestedListings] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim() || loading) return;

    const userMessage = query.trim();
    setQuery('');
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setLoading(true);

    try {
      const res = await api.post('/ai-search', { 
        query: userMessage, 
        language 
      });
      
      setMessages(prev => [...prev, { 
        type: 'assistant', 
        text: res.data.response,
        suggestions: res.data.suggested_listing_ids 
      }]);
      
      // Load suggested listings details
      if (res.data.suggested_listing_ids?.length > 0) {
        const listings = [];
        for (const id of res.data.suggested_listing_ids.slice(0, 3)) {
          try {
            const listingRes = await api.get(`/listings/${id}`);
            listings.push(listingRes.data);
          } catch (err) {
            // Skip if listing not found
          }
        }
        setSuggestedListings(listings);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        type: 'assistant', 
        text: language === 'fr' 
          ? "Désolé, je n'ai pas pu traiter votre demande. Réessayez plus tard."
          : "Sorry, I couldn't process your request. Please try again later."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQueries = language === 'fr' 
    ? [
        "Je cherche un studio meublé à Yaoundé",
        "Appartement avec WiFi à Douala moins de 30000 FCFA",
        "Logement près du centre-ville de Kribi"
      ]
    : [
        "I'm looking for a furnished studio in Yaoundé",
        "Apartment with WiFi in Douala under 30000 FCFA",
        "Accommodation near downtown Kribi"
      ];

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-4 h-14 w-14 rounded-full shadow-lg z-40 bg-gradient-to-br from-primary to-accent hover:opacity-90"
        data-testid="ai-assistant-btn"
      >
        <Sparkles className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <div className="fixed bottom-24 right-4 w-[380px] max-w-[calc(100vw-2rem)] z-40" data-testid="ai-assistant-panel">
      <Card className="shadow-2xl border-2 border-primary/20">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">
                {language === 'fr' ? 'Assistant Roomia' : 'Roomia Assistant'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {language === 'fr' ? 'Propulsé par IA' : 'AI Powered'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Messages */}
        <CardContent className="p-4 h-[350px] overflow-y-auto space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-6">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                {language === 'fr' 
                  ? 'Comment puis-je vous aider à trouver votre logement idéal ?'
                  : 'How can I help you find your ideal accommodation?'}
              </p>
              <div className="space-y-2">
                {suggestedQueries.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setQuery(q)}
                    className="block w-full text-left text-sm p-2 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    "{q}"
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                      msg.type === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-md'
                        : 'bg-secondary rounded-bl-md'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                  </div>
                </div>
              ))}

              {/* Suggested Listings */}
              {suggestedListings.length > 0 && (
                <div className="space-y-2 mt-4">
                  <p className="text-xs font-medium text-muted-foreground">
                    {language === 'fr' ? 'Logements suggérés:' : 'Suggested listings:'}
                  </p>
                  {suggestedListings.map(listing => (
                    <Link
                      key={listing.listing_id}
                      to={`/listings/${listing.listing_id}`}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 p-2 rounded-lg bg-background hover:bg-secondary/50 transition-colors border"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted shrink-0">
                        {listing.images?.[0] ? (
                          <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Home className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{listing.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {listing.price_per_night?.toLocaleString()} FCFA
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-secondary p-3 rounded-2xl rounded-bl-md">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input */}
        <form onSubmit={handleSearch} className="p-4 border-t bg-background">
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={language === 'fr' ? 'Décrivez votre logement idéal...' : 'Describe your ideal accommodation...'}
              disabled={loading}
              className="flex-1"
              data-testid="ai-search-input"
            />
            <Button type="submit" disabled={loading || !query.trim()} data-testid="ai-search-submit">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AISearchAssistant;
